import cn from 'classnames';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { CaretLeft, CaretRight } from 'src/common/icons';
import Dropdown from 'src/components/Dropdown';
import messages from './messages';
import styles from './Pager.module.scss';

type PagerProps = {
  currentPage: number;
  totalPages: number;
  onChange: (newPage: number) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  className?: string;
  showItemsPerPage?: boolean;
  datatest?: string;
};

const createRange = (from: number, to: number): number[] => [...Array(to - from)].map((_, i) => i + from);

const PerPageDropdown = ({
  onItemsPerPageChange,
  itemsPerPage,
}: Pick<PagerProps, 'itemsPerPage' | 'onItemsPerPageChange'>) => {
  return (
    <div className={styles.dropdown}>
      <Dropdown
        dropdownAnchor="right"
        dropdownDirection="up"
        onClick={onItemsPerPageChange}
        options={[12, 18, 30].map((e) => ({
          key: e,
          value: e,
          noBorderBottom: true,
        }))}
        className={styles.dropdown}
      >
        <FormattedMessage {...messages.show} values={{ items: itemsPerPage }} />
      </Dropdown>
    </div>
  );
};

export const Pager: React.FC<PagerProps> = ({
  currentPage,
  totalPages,
  onChange,
  itemsPerPage,
  onItemsPerPageChange,
  className,
  showItemsPerPage = true,
  datatest,
  ...rest
}) => {
  const [value, setValue] = React.useState(currentPage);

  const visiblePagesRange = createRange(currentPage - 3, currentPage + 4).filter((v) => v > 0 && v <= totalPages);

  const emitValue = (requestedPage: number) => {
    if (requestedPage !== currentPage) onChange(requestedPage);
  };

  React.useEffect(() => {
    setValue(currentPage);
  }, [currentPage]);

  if (totalPages < 2)
    return showItemsPerPage ? (
      <PerPageDropdown itemsPerPage={itemsPerPage} onItemsPerPageChange={onItemsPerPageChange} />
    ) : null;

  // Compact prev/next + counter pair used by CSS to swap in the
  // numbered list on small viewports. Keeping both in the DOM lets the
  // stylesheet pick which one to display so the component itself stays
  // agnostic to viewport size.
  const prevPage = Math.max(1, currentPage - 1);
  const nextPage = Math.min(totalPages, currentPage + 1);

  return (
    <div className={cn([styles.Pager, className])}>
      <div className={cn(styles.buttons, styles.buttonsFull)}>
        {visiblePagesRange[0] > 1 && (
          <button onClick={() => emitValue(1)} className={cn([styles.button, { [styles.active]: currentPage === 1 }])} data-test={datatest}>
            1
          </button>
        )}
        {visiblePagesRange[0] > 2 && <span>&hellip;</span>}
        {visiblePagesRange.map((page, idx) => (
          <button
            key={idx}
            data-test={datatest}
            onClick={() => emitValue(page)}
            className={cn([styles.button, { [styles.active]: currentPage === page }])}
          >
            {page}
          </button>
        ))}
        {visiblePagesRange[visiblePagesRange.length - 1] < totalPages - 1 && <span>&hellip;</span>}
        {visiblePagesRange[visiblePagesRange.length - 1] < totalPages && (
          <button
            data-test={datatest}
            onClick={() => emitValue(totalPages)}
            className={cn([styles.button, { [styles.active]: currentPage === totalPages }])}
          >
            {totalPages}
          </button>
        )}
      </div>
      <div className={cn(styles.buttons, styles.buttonsCompact)} aria-hidden>
        <button
          type="button"
          className={cn(styles.button, styles.compactNav)}
          onClick={() => emitValue(prevPage)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <CaretLeft />
        </button>
        <span className={styles.compactCounter}>
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          className={cn(styles.button, styles.compactNav)}
          onClick={() => emitValue(nextPage)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          <CaretRight />
        </button>
      </div>
      {showItemsPerPage && <PerPageDropdown itemsPerPage={itemsPerPage} onItemsPerPageChange={onItemsPerPageChange} />}
    </div>
  );
};

export default Pager;
